import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import { PageHero } from './UniversitiesListPage';
import { Target, Award, Users, Globe2, Heart, ShieldCheck } from 'lucide-react';

const VALUES = [
  { icon: <Target className="w-6 h-6" />, t: 'Student First', d: 'Every recommendation is made with your career outcome — not commission — in mind.' },
  { icon: <ShieldCheck className="w-6 h-6" />, t: 'Trusted Network', d: 'Partner universities are individually vetted before joining our network.' },
  { icon: <Heart className="w-6 h-6" />, t: 'End-to-End Care', d: 'From first call to first day on campus — we walk the journey with you.' },
];

const AboutPage = () => (
  <PublicLayout>
    <PageHero title="About Edu Bridge Network" subtitle="A modern study-abroad partner for ambitious students worldwide." />

    <section className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-[#F5A623] font-semibold tracking-widest text-xs uppercase">Our Story</p>
        <h2 className="mt-2 text-3xl font-bold text-[#1A2A4F]">Bridging the gap between students and world-class universities</h2>
        <p className="mt-4 text-gray-600 leading-relaxed">
          Edu Bridge Network started with a simple frustration: the international admissions process is opaque, agent-dependent, and stacked against the student. We built EBN to flip that dynamic — putting transparent, AI-augmented tooling alongside human counsellors who actually pick up the phone.
        </p>
        <p className="mt-3 text-gray-600 leading-relaxed">
          Today we work with 60+ partner universities across 10 countries, helping hundreds of students each year secure offers, scholarships, and visas at top institutions.
        </p>
        <div className="mt-6 flex gap-3">
          <Link to="/contact" className="bg-[#F5A623] text-white font-semibold px-6 py-3 rounded-md hover:brightness-95" data-testid="about-cta-contact">Talk to us</Link>
          <Link to="/universities" className="bg-[#1A2A4F] text-white font-semibold px-6 py-3 rounded-md hover:opacity-90" data-testid="about-cta-universities">Browse universities</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard icon={<Users />} value="2,500+" label="Students placed" />
        <StatCard icon={<Globe2 />} value="10+" label="Countries" />
        <StatCard icon={<Award />} value="60+" label="Partner unis" />
        <StatCard icon={<Heart />} value="98%" label="Visa success" />
      </div>
    </section>

    <section className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <p className="text-[#F5A623] font-semibold tracking-widest text-xs uppercase">What we stand for</p>
          <h2 className="mt-2 text-3xl font-bold text-[#1A2A4F]">Our values</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <div key={v.t} className="bg-white p-7 rounded-xl border border-gray-100 hover:border-[#F5A623] transition" data-testid={`value-${v.t.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 rounded-full bg-[#F5A623]/10 text-[#F5A623] flex items-center justify-center">{v.icon}</div>
              <h3 className="mt-4 font-bold text-lg text-[#1A2A4F]">{v.t}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#1A2A4F] text-white">
      <div className="max-w-7xl mx-auto px-4 py-14 text-center">
        <h2 className="text-3xl font-bold">Ready to take the next step?</h2>
        <p className="mt-3 opacity-90 max-w-xl mx-auto">A 15-minute call with our counsellors is the fastest way to shortlist the right programs for you.</p>
        <Link to="/contact" className="mt-6 inline-block bg-[#F5A623] text-white font-semibold px-7 py-3 rounded-md hover:brightness-95" data-testid="about-bottom-cta">Book a free consultation</Link>
      </div>
    </section>
  </PublicLayout>
);

const StatCard = ({ icon, value, label }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5 text-center">
    <div className="w-10 h-10 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center mx-auto">
      {icon}
    </div>
    <div className="mt-3 text-2xl font-bold text-[#1A2A4F]">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

export default AboutPage;
